import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, TemplateRef } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import { PaginationConfig, TableColumn } from '../data-table/data-table.component';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { LoadingStateComponent } from '../loading-state/loading-state.component';

@Component({
  selector: 'app-table-mobile-card-list',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    EmptyStateComponent,
    LoadingStateComponent
  ],
  templateUrl: './table-mobile-card-list.component.html',
  styleUrl: './table-mobile-card-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableMobileCardListComponent<T> {
  @Input() columns: TableColumn[] = [];
  @Input() data: T[] | null | undefined = [];
  @Input() cellTemplate?: TemplateRef<any>;
  @Input() cardTemplate?: TemplateRef<{ $implicit: T; row: T }>;
  @Input() rowDetailTemplate?: TemplateRef<any>;
  @Input() expandedRow: T | null = null;
  @Input() isLoading = false;
  @Input() errorMessage = '';
  @Input() noDataMessage = 'No se encontraron registros';
  @Input() paginationConfig?: PaginationConfig;
  @Input() pageSizeOptions?: number[] = [5, 10, 25, 50, 100];
  @Input() showPageSizeSelector = false;
  @Input() enableRowHover = true;

  @Output() rowClick = new EventEmitter<T>();
  @Output() pageChange = new EventEmitter<{ page: number }>();
  @Output() pageSizeChange = new EventEmitter<{ pageSize: number }>();

  get rows(): T[] {
    return this.data ?? [];
  }

  get hasData(): boolean {
    return this.rows.length > 0;
  }

  get mobileColumns(): TableColumn[] {
    return [...(this.columns ?? [])]
      .filter(column => column.visible !== false && column.mobileHidden !== true)
      .sort((a, b) => this.getMobileOrder(a) - this.getMobileOrder(b));
  }

  get titleColumn(): TableColumn | undefined {
    return this.mobileColumns.find(column => column.mobileTitle) ?? this.mobileColumns[0];
  }

  get detailColumns(): TableColumn[] {
    const titleKey = this.titleColumn?.key;
    return this.mobileColumns.filter(column => column.key !== titleKey);
  }

  get isFirstPage(): boolean {
    return !this.paginationConfig || this.paginationConfig.currentPage <= 1;
  }

  get isLastPage(): boolean {
    return !this.paginationConfig || this.paginationConfig.currentPage >= this.paginationConfig.totalPages;
  }

  get selectedPageSize(): number | undefined {
    return this.paginationConfig?.pageSize;
  }

  onRowClick(row: T): void {
    if (this.enableRowHover) {
      this.rowClick.emit(row);
    }
  }

  previousPage(): void {
    if (!this.paginationConfig || this.isFirstPage || this.isLoading) {
      return;
    }

    this.pageChange.emit({ page: this.paginationConfig.currentPage - 1 });
  }

  nextPage(): void {
    if (!this.paginationConfig || this.isLastPage || this.isLoading) {
      return;
    }

    this.pageChange.emit({ page: this.paginationConfig.currentPage + 1 });
  }

  onPageSizeChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (!Number.isNaN(value)) {
      this.pageSizeChange.emit({ pageSize: value });
    }
  }

  getColumnLabel(column: TableColumn): string {
    return column.mobileLabel || column.header;
  }

  getFallbackValue(row: T, column: TableColumn): string {
    const value = (row as any)?.[column.key];
    return value === null || value === undefined || value === '' ? '-' : String(value);
  }

  trackByRow(index: number, row: T): unknown {
    return (row as any)?.id ?? (row as any)?.key ?? index;
  }

  trackByColumn(_index: number, column: TableColumn): string {
    return column.key;
  }

  private getMobileOrder(column: TableColumn): number {
    return column.mobileOrder ?? this.columns.indexOf(column);
  }
}
